import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import '../styles/SkillsDevelopmentDocumentation.scss';
import { blurOnEnter } from '../utils/keyboard';
import { useSectorElementKey } from '../utils/useSectorElementKey';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const buildFyMonths = (startDate) => {
  if (!startDate) return MONTH_NAMES;
  const startMonthIndex = Number(startDate.split('-')[1]) - 1;
  if (isNaN(startMonthIndex) || startMonthIndex < 0 || startMonthIndex > 11) return MONTH_NAMES;
  return Array.from({ length: 12 }, (_, i) => MONTH_NAMES[(startMonthIndex + i) % 12]);
};

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

// Per-element display metadata. Adding a new sector's skills development variant means adding one entry here, not a new page file.

const SUBMIT_META = {
  skills_development: {
    pageDesc: 'Skills Development - Upload and calculate score',
    templateFilename: 'Skills Development Data Sheet',
    cardDesc: 'Skills Development Data Sheet (.xlsx)',
    backPath: '/skills-development/',
    scorecardPath: '/skills-development/scorecard',
  },
  skills_development_transport: {
    pageDesc: 'Skills Development (Transport) - Upload and calculate score',
    templateFilename: 'ROad Freight Skills Development Data Sheet',
    cardDesc: 'Road Freight Skills Development Data Sheet (.xlsx)',
    backPath: '/skills-development/',
    scorecardPath: '/skills-development/scorecard/skills-development-transport',
  },
};

// The sector variants of this element; useSectorElementKey picks the one this company has.
const SD_VARIANTS = ['skills_development', 'skills_development_transport', 'skills_development_specialised'];

const SDSubmitDataSheet = () => {
  const navigate = useNavigate();
  const { elementKey: rawElementKey } = useParams();
  const requestedKey = (rawElementKey || 'skills_development').replace(/-/g, '_');
  const elementKey = useSectorElementKey(requestedKey, SD_VARIANTS); // null until resolved
  const meta = SUBMIT_META[elementKey || requestedKey] || SUBMIT_META.skills_development;

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [leviableType, setLeviableType] = useState('payroll');
  const [payrollAmount, setPayrollAmount] = useState('');
  const [emp201Amounts, setEmp201Amounts] = useState(Array(12).fill(''));
  const [months, setMonths] = useState(MONTH_NAMES);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved'
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get('/core/skills-development/settings/')
      .then(res => {
        const data = res.data;
        if (data.leviable_type) setLeviableType(data.leviable_type);
        if (data.payroll_amount) setPayrollAmount(String(data.payroll_amount));
        if (data.emp201_amounts?.length === 12) setEmp201Amounts(data.emp201_amounts.map(String));
      })
      .catch(() => {});

    api.get('/core/financial-years/current/')
      .then(res => setMonths(buildFyMonths(res.data?.start_date)))
      .catch(() => {});
  }, []);
  
  useEffect(() => {
    if (!elementKey) return;
    api.get(`/core/scorecard/get_scorecard/?element=${elementKey}`)
      .then(res => setResult(res.data))
      .catch(() => {});
  }, [elementKey]);

  const handleSave = async () => {
    setSaveStatus('saving');
    setError(null);
    try {
      await api.post('/core/skills-development/settings/save/', {
        leviable_type: leviableType,
        payroll_amount: Number(payrollAmount) || 0,
        emp201_amounts: emp201Amounts.map(v => Number(v) || 0),
      });
      setDirty(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setError('Failed to save the leviable amount. Please try again.');
      setSaveStatus('idle');
    }
  };

  const allEmp201Filled = emp201Amounts.every(v => v !== '' && !isNaN(Number(v)) && Number(v) >= 0);
  const usingFallback = leviableType === 'emp201' && !allEmp201Filled;

  // EMP201 figures are the monthly 1% SDL contributions, so the leviable
  // amount (underlying payroll) is their sum x 100.
  const effectiveLeviableAmount =
    leviableType === 'emp201' && allEmp201Filled
      ? emp201Amounts.reduce((sum, v) => sum + Number(v), 0) * 100
      : Number(payrollAmount) || 0;

  const handleEmp201Change = (index, value) => {
    const updated = [...emp201Amounts];
    updated[index] = value;
    setEmp201Amounts(updated);
    setDirty(true);
  };

  const handleDownloadTemplate = async () => {
    if (!elementKey) return;
    try {
      const res = await api.get(`/core/scorecard/download_template/?element=${elementKey}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Skills Development Data Sheet Template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Template not found on server.');
    }
  };

  const handleUploadClick = () => {
    if (!effectiveLeviableAmount) {
      setError('Please enter a leviable amount before uploading.');
      return;
    }
    // The calculation reads the saved leviable amount, not what's on screen.
    if (dirty) {
      setError('Save the leviable amount before uploading.');
      return;
    }
    setError(null);
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !elementKey) return;

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('element', elementKey);
      const res = await api.post('/core/scorecard/calculate/', formData);
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 422) {
        setError('No EAP targets found for your province. Update your province in Company Information.');
      } else {
        setError(err.response?.data?.error || 'Failed to process the data sheet. Check the file format and try again.');
      }
    } finally {
      setUploading(false);
      fileInputRef.current.value = '';
    }
  };

  const maxPoints = (result?.indicators || []).reduce((s, ind) => s + (ind.points || 0), 0);
  
  return (
    <div className="od-container">
      <div className="od-header">
        <button className="od-back" onClick={() => navigate(meta.backPath)}>← Back</button>
        <div>
          <h1>Submit Data Sheet</h1>
          <p>{meta.pageDesc}</p>
        </div>
      </div>

      <div className="od-section-label">Leviable Amount</div>

      <div className="od-leviable">
        <div className="od-toggle">
          <button
            type="button"
            className={`od-toggle-btn${leviableType === 'payroll' ? ' od-toggle-btn--active' : ''}`}
            onClick={() => { if (leviableType !== 'payroll') { setLeviableType('payroll'); setDirty(true); } }}
          >
            Annual Payroll
          </button>
          <button
            type="button"
            className={`od-toggle-btn${leviableType === 'emp201' ? ' od-toggle-btn--active' : ''}`}
            onClick={() => { if (leviableType !== 'emp201') { setLeviableType('emp201'); setDirty(true); } }}
          >
            EMP201 (12 Months)
          </button>
        </div>

        {leviableType === 'payroll' && (
          <div className="od-leviable-row">
            <label className="od-leviable-label">Annual Payroll (R)</label>
            <input
              className="od-leviable-input"
              type="number"
              min="0"
              placeholder="e.g. 5000000"
              value={payrollAmount}
              onChange={e => { setPayrollAmount(e.target.value); setDirty(true); }}
              onKeyDown={blurOnEnter}
            />
          </div>
        )}

        {leviableType === 'emp201' && (
          <>
            <div className="od-emp201-grid">
              {months.map((month, i) => (
                <div key={`${month}-${i}`} className="od-emp201-month">
                  <label>{month}</label>
                  <input
                    className="od-emp201-input"
                    type="number"
                    min="0"
                    placeholder="R"
                    value={emp201Amounts[i]}
                    onChange={e => handleEmp201Change(i, e.target.value)}
                    onKeyDown={blurOnEnter}
                  />
                </div>
              ))}
            </div>

            {usingFallback && (
              <div className="od-leviable-fallback">
                Not all 12 months are filled in — Annual Payroll will be used as the leviable amount.
                <div className="od-leviable-row">
                  <label className="od-leviable-label">Annual Payroll (R)</label>
                  <input
                    className="od-leviable-input"
                    type="number"
                    min="0"
                    placeholder="e.g. 5000000"
                    value={payrollAmount}
                    onChange={e => { setPayrollAmount(e.target.value); setDirty(true); }}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {effectiveLeviableAmount > 0 && (
          <div className="od-leviable-summary">
            Leviable amount: <strong>R {effectiveLeviableAmount.toLocaleString('en-ZA')}</strong>
            {leviableType === 'emp201' && allEmp201Filled && ' (sum of 12 months × 100)'}
            {usingFallback && ' (annual payroll fallback)'}
          </div>
        )}

        <div className="od-leviable-actions">
          {dirty && saveStatus !== 'saving' && <span className="od-leviable-unsaved">Unsaved changes</span>}
          <button
            type="button"
            className="od-save-btn"
            onClick={handleSave}
            disabled={saveStatus === 'saving' || (!dirty && saveStatus !== 'saved')}
          >
            {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save'}
          </button>
        </div>
      </div>

      <div className="od-section-label" style={{ marginTop: 32 }}>
        Download the template, fill it in, then upload to calculate your scorecard
      </div>

      <div className="od-grid">
        <button className="od-card" onClick={handleDownloadTemplate}>
          <div className="od-card-icon"><DownloadIcon /></div>
          <span className="od-card-label">Download Template</span>
          <span className="od-card-desc">{meta.cardDesc}</span>
        </button>

        <button
          className={`od-card ${result ? 'od-card--uploaded' : ''}`}
          onClick={handleUploadClick}
          disabled={uploading}
        >
          <div className="od-card-icon"><UploadIcon /></div>
          <span className="od-card-label">
            {uploading ? 'Calculating…' : 'Upload Completed Sheet'}
          </span>
          {result && (
            <span className="od-card-status">✓ {result.filename}</span>
          )}
        </button>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        accept=".xlsx"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {error && (
        <div className="od-error">
          {error}
          {error.includes('province') && (
            <button className="od-error-link" onClick={() => navigate('/finance-statutory/company-information')}>
              Update Company Information
            </button>
          )}
        </div>
      )}

      {result && (
        <div className="od-result">
          <div className="od-result-header">
            <strong>Calculation complete</strong>
            <span>Province: {result.province}</span>
          </div>
          <div className="od-result-score">
            <span className="od-result-points">{result.total_points}</span>
            {maxPoints > 0 && <span className="od-result-max">/ {maxPoints}</span>}
          </div>
          <button
            className="od-result-btn"
            onClick={() => navigate(meta.scorecardPath)}
          >
            View Full Scorecard →
          </button>
        </div>
      )}
    </div>
  );
};

export default SDSubmitDataSheet;
